import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/endpoints';
import Pagination from '@/components/common/Pagination';
import { Alert, Empty, ErrorState, Loading } from '@/components/common/State';
import { formatNumber, formatWon } from '@/utils/format';
import type { ProductStatus } from '@/api/types';

const STATUS_LABEL: Record<ProductStatus, string> = {
  DRAFT: '임시저장',
  ON_SALE: '판매중',
  SOLD_OUT: '품절',
  HIDDEN: '숨김',
};

export default function AdminProductListPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'products', page, keyword],
    queryFn: () => adminApi.products({ page, size: 20, keyword: keyword || undefined }),
  });

  const detail = useQuery({
    queryKey: ['admin', 'product', expanded],
    queryFn: () => adminApi.productDetail(expanded!),
    enabled: expanded !== null,
  });

  const changeStatus = useMutation({
    mutationFn: ({ productId, status }: { productId: number; status: string }) =>
      adminApi.changeProductStatus(productId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      setFeedback('판매 상태를 변경했습니다.');
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const setFeatured = useMutation({
    mutationFn: ({ productId, featured }: { productId: number; featured: boolean }) =>
      adminApi.setProductFeatured(productId, featured),
    onSuccess: (_, { featured }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      setFeedback(featured ? '메인 추천에 노출합니다.' : '메인 추천에서 내렸습니다.');
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const removeProduct = useMutation({
    mutationFn: (productId: number) => adminApi.deleteProduct(productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      setFeedback('상품을 삭제했습니다. (주문 이력 보존을 위해 숨김 처리됩니다)');
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const adjustStock = useMutation({
    mutationFn: (body: { optionId: number; changeQty: number; changeType: string; memo?: string }) =>
      adminApi.adjustStock(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'product', expanded] });
      setFeedback('재고를 조정했습니다.');
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 className="panel-title" style={{ marginBottom: 0 }}>
          상품 · 재고 관리
        </h1>
        <div style={{ flex: 1 }} />
        <Link to="/admin/products/new" className="btn btn--primary btn--sm">
          + 상품 등록
        </Link>
      </div>
      {feedback && <Alert tone="info">{feedback}</Alert>}

      <input
        className="input"
        style={{ maxWidth: 260, marginTop: 16, marginBottom: 16 }}
        placeholder="상품명 검색"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setKeyword(e.currentTarget.value);
            setPage(1);
          }
        }}
      />

      {isLoading && <Loading />}
      {isError && <ErrorState error={error} onRetry={refetch} />}

      {data &&
        (data.content.length === 0 ? (
          <Empty title="등록된 상품이 없습니다." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상품명</th>
                    <th>카테고리</th>
                    <th>판매가</th>
                    <th>판매량</th>
                    <th>평점</th>
                    <th>상태</th>
                    <th>추천</th>
                    <th>재고</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((product) => (
                    <tr key={product.productId}>
                      <td>{product.productId}</td>
                      <td className="wrap">{product.name}</td>
                      <td>{product.categoryName}</td>
                      <td>{formatWon(product.sellingPrice)}</td>
                      <td>{formatNumber(product.salesCount)}</td>
                      <td>
                        {product.ratingAverage.toFixed(1)} ({product.reviewCount})
                      </td>
                      <td>
                        <select
                          className="select"
                          style={{ width: 110, padding: '4px 8px' }}
                          value={product.status}
                          onChange={(e) =>
                            changeStatus.mutate({ productId: product.productId, status: e.target.value })
                          }
                        >
                          {Object.entries(STATUS_LABEL).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="checkbox"
                            checked={product.featured}
                            disabled={setFeatured.isPending}
                            onChange={(e) =>
                              setFeatured.mutate({
                                productId: product.productId,
                                featured: e.target.checked,
                              })
                            }
                          />
                          {product.featured && <span className="badge badge--info">노출</span>}
                        </label>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--outline btn--sm"
                          onClick={() =>
                            setExpanded(expanded === product.productId ? null : product.productId)
                          }
                        >
                          {expanded === product.productId ? '닫기' : '옵션/재고'}
                        </button>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Link
                            to={`/admin/products/${product.productId}/edit`}
                            className="btn btn--outline btn--sm"
                          >
                            수정
                          </Link>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={removeProduct.isPending}
                            onClick={() => {
                              if (window.confirm(`'${product.name}' 을(를) 삭제할까요?`)) {
                                removeProduct.mutate(product.productId);
                              }
                            }}
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {expanded !== null && (
              <div className="card" style={{ marginTop: 16 }}>
                <div className="card__body">
                  <h2 style={{ fontSize: 16, marginTop: 0 }}>옵션 재고</h2>
                  {detail.isLoading && <Loading />}
                  <div className="table-wrap">
                    {detail.data && (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>옵션</th>
                            <th>SKU</th>
                            <th>추가금</th>
                            <th>재고</th>
                            <th>입고/조정</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.data.options.map((option) => (
                            <tr key={option.optionId}>
                              <td className="wrap">{option.name}</td>
                              <td>{option.sku}</td>
                              <td>{formatWon(option.additionalPrice)}</td>
                              <td>
                                {option.stockQuantity}
                                {option.soldOut && <span className="badge badge--danger">품절</span>}
                                {option.lowStock && <span className="badge badge--warn">부족</span>}
                              </td>
                              <td>
                                <StockAdjustForm
                                  onSubmit={(changeQty, memo) =>
                                    adjustStock.mutate({
                                      optionId: option.optionId,
                                      changeQty,
                                      changeType: changeQty >= 0 ? 'INBOUND' : 'ADJUST',
                                      memo,
                                    })
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}

            <Pagination page={data.page} totalPages={data.totalPages} onChange={setPage} />
          </>
        ))}
    </>
  );
}

function StockAdjustForm({ onSubmit }: { onSubmit: (changeQty: number, memo?: string) => void }) {
  const [qty, setQty] = useState('');
  const [memo, setMemo] = useState('');

  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        className="input"
        style={{ width: 80 }}
        type="number"
        placeholder="±수량"
        value={qty}
        onChange={(e) => setQty(e.target.value)}
      />
      <input
        className="input"
        style={{ width: 120 }}
        placeholder="메모"
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
      />
      <button
        type="button"
        className="btn btn--primary btn--sm"
        disabled={!qty || Number.isNaN(Number(qty))}
        onClick={() => {
          onSubmit(Number(qty), memo || undefined);
          setQty('');
          setMemo('');
        }}
      >
        반영
      </button>
    </div>
  );
}
